/**
 * ValidationDashboard - Insole validation report display
 * 
 * Shows validation checks with color-coded pass/fail indicators,
 * metrics, and action buttons for approval or re-editing.
 */

import React from 'react'
import type { ValidationReport, ValidateResponse } from '../../../services/types'
import './ValidationDashboard.css'

interface ValidationDashboardProps {
  /** Validation response from backend */
  validation: ValidateResponse | null
  /** Inline validation report (from GenerateResponse) */
  report?: ValidationReport | null
  /** Metrics from generation */
  metrics?: Record<string, any> | null
  /** Callback to approve and export */
  onApproveExport?: () => void
  /** Callback to override and approve (for REVIEW verdicts) */
  onOverrideApprove?: () => void
  /** Callback to return to boundary editing */
  onReturnToEdit?: () => void
  /** Loading state */
  isLoading?: boolean
}

function getVerdict(report: ValidationReport | null | undefined): 'pass' | 'review' | 'fail' {
  if (!report) return 'review'
  if (report.passed) return 'pass'
  const failedCount = report.checks.filter(c => !c.passed).length
  if (failedCount > report.checks.length / 2) return 'fail'
  return 'review'
}

function getVerdictLabel(verdict: 'pass' | 'review' | 'fail'): string {
  switch (verdict) {
    case 'pass': return 'PASS'
    case 'review': return 'REVIEW NEEDED'
    case 'fail': return 'FAIL'
  }
}

export const ValidationDashboard: React.FC<ValidationDashboardProps> = ({
  validation,
  report: inlineReport,
  metrics: inlineMetrics,
  onApproveExport,
  onOverrideApprove,
  onReturnToEdit,
  isLoading = false
}) => {
  const report = validation?.validation_report ?? inlineReport
  const metrics = validation?.metrics ?? inlineMetrics
  const verdict = getVerdict(report)

  return (
    <div className="validation-dashboard">
      <h3>Validation Report</h3>

      <div className={`verdict-banner verdict-${verdict}`}>
        <span className="verdict-icon">
          {verdict === 'pass' ? '✅' : verdict === 'review' ? '⚠️' : '❌'}
        </span>
        <span className="verdict-label">{getVerdictLabel(verdict)}</span>
      </div>

      {report && (
        <div className="checks-list">
          {report.checks.map((check, i) => (
            <div key={i} className={`check-row ${check.passed ? 'check-pass' : 'check-fail'}`}>
              <span className="check-icon">{check.passed ? '●' : '○'}</span>
              <span className="check-name">{check.name}</span>
              {check.value !== undefined && check.value !== null && (
                <span className="check-value">{typeof check.value === 'number' ? check.value.toFixed(2) : check.value}</span>
              )}
              {check.detail && <span className="check-detail">{check.detail}</span>}
            </div>
          ))}
        </div>
      )}

      {report && report.repairs.length > 0 && (
        <div className="repairs-section">
          <h4>Applied Repairs</h4>
          <ul>
            {report.repairs.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}

      {metrics && Object.keys(metrics).length > 0 && (
        <div className="metrics-section">
          <h4>Metrics</h4>
          <div className="metrics-grid">
            {Object.entries(metrics).map(([key, value]) => (
              <div key={key} className="metric-item">
                <span className="metric-label">{key.replace(/_/g, ' ')}</span>
                <span className="metric-value">
                  {typeof value === 'number' ? value.toFixed(3) : String(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="validation-actions">
        {verdict === 'pass' && onApproveExport && (
          <button
            onClick={onApproveExport}
            disabled={isLoading}
            className="action-button action-approve"
          >
            Approve & Export
          </button>
        )}
        {verdict === 'review' && onOverrideApprove && (
          <button
            onClick={onOverrideApprove}
            disabled={isLoading}
            className="action-button action-override"
          >
            Override & Approve
          </button>
        )}
        {(verdict === 'fail' || verdict === 'review') && onReturnToEdit && (
          <button
            onClick={onReturnToEdit}
            disabled={isLoading}
            className="action-button action-return"
          >
            Return to Edit
          </button>
        )}
      </div>
    </div>
  )
}
